import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { TProduct } from './ProductDetail';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TProductStickyPanelInstance } from 'widgets/product/ProductStickyPanel';

type TProcessButtonPrefs = ReturnType<TProcessButtonInstance['prefs']>;

export interface IAddToCartButton extends TProcessButtonInstance {
    prefs(): TProcessButtonPrefs & {
        isOutOfStock?: boolean;
        stock: string;
    };
}

/**
 * @description Base AddToCartStickyMixin implementation
 * @classdesc Allows to buy products from the sticky panel while the user scrolls a page and the main "Add to Cart" button is not in the viewport
 * <br>This class is not intended to have a separate DOM representation.
 * @param AddToCartMixin Base widget for extending
 * @returns Add to Cart Sticky mixin widget
 */
function AddToCartStickyMixinClassCreator(AddToCartMixin: import('widgets/product/AddToCartMixin').TAddToCartMixin) {
    /**
     * @category widgets
     * @subcategory product
     * @class AddToCartStickyMixin
     * @augments AddToCartMixin
     */
    class AddToCartStickyMixin extends AddToCartMixin {
        observer: IntersectionObserver | undefined;

        stickyPanel: TProductStickyPanelInstance | undefined;

        isStickyPanelShown = false;

        stickyPanelButton: TProcessButtonInstance | undefined;

        prefs() {
            return {
                classesHidden: 'm-hidden',
                ...super.prefs()
            };
        }

        /**
         * @description Attach IntersectionObserver to check if the main "Add to Cart" button is in the user's viewport
         */
        initIntersectionObserver() {
            this.getById('productStickyPanel', (stickyPanel: TProductStickyPanelInstance) => {
                this.stickyPanel = stickyPanel;

                if (!stickyPanel) { return; }

                this.getById<IAddToCartButton>(this.prefs().addToCartBtn, (addToCartBtn) => {
                    const addToCartBtnElement = addToCartBtn.ref('self').get();

                    if (!addToCartBtnElement) { return; }

                    this.observer = new IntersectionObserver(
                        ([entry]) => {
                            if (!entry.isIntersecting && addToCartBtnElement.getBoundingClientRect().top < 0) {
                                this.isStickyPanelShown = true;
                            } else {
                                this.isStickyPanelShown = false;
                            }

                            stickyPanel.togglePanel(this.isStickyPanelShown);
                        },
                        {
                            threshold: 0
                        }
                    );

                    this.observer.observe(addToCartBtnElement);
                });
            });
        }

        /**
         * @description Attach IntersectionObserver to carousel to pause autoplay in case if carousel is not visible
         * @param panelButton - "Add to Cart" button from the sticky panel to future highlighting the process of adding product to the cart
         */
        addToCartByPanel(panelButton: TProcessButtonInstance) {
            this.stickyPanelButton = panelButton;

            this.getById<IAddToCartButton>(this.prefs().addToCartBtn, (addToCartBtn) => {
                addToCartBtn.handleClick();
            });
        }

        /**
         * @description Show message
         * @param msg Message
         * @param error Error flag
         * @param bundleItemMsg Bundle item widget
         */
        showCartMessage(msg: string, error = false, bundleItemMsg?) {
            this.getById(this.prefs().addToCartMsg, (addToCartMsg) => {
                if (typeof this.spacesLength === 'undefined') {
                    this.spacesLength = 0;
                }

                // It's needed to force render previously added success message. If we try to add the same product
                // several times, success message alert will not be announced by a screen reader.
                msg = msg.concat(Array(++this.spacesLength).join(this.prefs().zeroWidthSpaceSymbol));

                addToCartMsg.render(
                    'template',
                    { msg, error },
                    bundleItemMsg ? bundleItemMsg.ref('container') : addToCartMsg.ref('container')
                ).then(
                    () => {
                        if (bundleItemMsg) {
                            bundleItemMsg.show();
                        } else {
                            addToCartMsg.show();
                        }

                        if (this.isStickyPanelShown && error && !bundleItemMsg) {
                            scrollWindowTo(addToCartMsg.ref('container').get(), false, true);
                        }
                    }
                );
            });
        }

        /**
         * @description Handles add to cart button click
         * @param button Add Product response
         * @param onBeforeSubmit Callback that executes before the request is submitted
         * @returns Promise object represents server response for session continuation
         */
        addToCart(button: TProcessButtonInstance, onBeforeSubmit?: () => void): Promise<unknown | null> {
            return super.addToCart(button, () => {
                if (typeof onBeforeSubmit === 'function') {
                    onBeforeSubmit();
                }

                this.startStickyPanelButtonLoader();
            }).finally(() => this.stopStickyPanelButtonLoader());
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TProduct) {
            super.updateProductView(product);

            this.renderStickyPanel(product);
        }

        /**
         * @description Render sticky panel blocks
         * @param product productModel
         */
        renderStickyPanel(product: TProduct) {
            if (this.stickyPanel) {
                this.stickyPanel.ref('self').toggleClass(this.prefs().classesHidden, !product.available);

                if (!product.available) { return; }

                this.renderStickyPanelImage(product);
                this.renderStickyPanelName(product.productName);
                this.renderStickyPanelPrice(product);
            }
        }

        /**
         * @description Render sticky panel image
         * @param product productModel
         */
        renderStickyPanelImage(product) {
            if (this.stickyPanel && product.images?.small) {
                this.stickyPanel.render(
                    'panelPictureTemplate',
                    product.images.small[0],
                    this.stickyPanel.ref('stickyPanelPicture')
                );
            }
        }

        /**
         * @description Render sticky panel name
         * @param productName Product name
         */
        renderStickyPanelName(productName: string) {
            if (this.stickyPanel) {
                this.stickyPanel.ref('stickyPanelName').setText(productName);
            }
        }

        /**
         * @description Render sticky panel price
         * @param product productModel
         */
        renderStickyPanelPrice(product: TProduct) {
            if (this.stickyPanel) {
                if (product.price && product.price.html) {
                    this.stickyPanel.getById('priceBlock', (priceBlock) => {
                        priceBlock.render('template', {}, undefined, product.price.html);
                    });
                }
            }
        }

        /**
         * @description Start sticky panel button loading animation
         */
        startStickyPanelButtonLoader() {
            if (this.isStickyPanelShown && this.stickyPanelButton) {
                this.stickyPanelButton.startProcess();
            }
        }

        /**
         * @description Stop sticky panel button loading animation
         */
        stopStickyPanelButtonLoader() {
            if (this.stickyPanelButton) {
                this.stickyPanelButton.stopProcess();
            }
        }
    }

    return AddToCartStickyMixin;
}

export type TAddToCartStickyMixin = ReturnType<typeof AddToCartStickyMixinClassCreator>;

export type TAddToCartStickyMixinInstance = InstanceType<TAddToCartStickyMixin>;

export default AddToCartStickyMixinClassCreator;
